/**
 * designDoc — the Design document schema, validator, and binding resolver.
 *
 * THE DESIGN DOC IS THE SINGLE SOURCE OF TRUTH (plain JSON, versioned). It is
 * rendered by Konva on the CLIENT (interactive editor) AND on the SERVER
 * (node-canvas) through the SHARED layerToKonva mapping, so preview ==
 * published. This module owns the shape + the validate gate + the binding
 * resolver.
 *
 * ESM module, ZERO node-only deps (Node 24 require()-of-ESM loads it server-side): it is
 * imported by BOTH the CRA client bundle and the server. The renderer injects
 * konva/canvas; this file only describes + validates + resolves data.
 *
 * Mirrors graph/validate.js: one structural gate, throws on the FIRST defect
 * with a precise message, no mutation, no fallbacks (value-4: fail loud/early).
 *
 * @module render/designDoc
 */

import { normalizeDesignDocFonts } from './brandFont.js';
import { FREE_PRICE_LABEL, priceMajorUnits, priceNumberText } from './priceText.js';

// Layer types the engine knows how to render (both ends).
const TYPES = ['image', 'text', 'badge', 'shape'];
const PLATFORMS = ['instagram', 'facebook', 'linkedin'];

// The kinds a VARIABLE (input-bound) layer can take from a wired node input. A
// layer with `source:{kind:'input', as}` is fed by the design node's matching
// input port at runtime: `image` -> the layer's `src`, `text` -> the layer's
// `text`, `link` -> the layer's `href` (a tappable target, e.g. the IG-Story CTA).
// Absent `source` => a BAKED layer (static src/text) or a context-bound layer
// (`bind:'context.…'`). ONE layer model: every layer is positioned identically;
// only where its content comes from differs (baked | input | context).
const INPUT_SLOT_KINDS = ['image', 'text', 'link'];

// The alignment values BOTH renderers honour. `align`/`verticalAlign` are passed
// straight into the Konva Text shape (layerToKonva), and the server runs that
// SAME shape under node-canvas — so there is exactly one implementation and
// nothing to keep in sync. Konva itself has no validator on those setters: an
// unknown value falls through its if/else chain and renders silently left/top,
// which is a fallback inside owned code. Gate it here instead, so the Inspector's
// segmented control and any LLM-authored doc share one vocabulary.
// 'justify' is deliberately excluded — a Konva-only mode with no design use here
// that changes the per-line width math.
const TEXT_ALIGNS = ['left', 'center', 'right'];
const TEXT_VERTICAL_ALIGNS = ['top', 'middle', 'bottom'];

// The legacy scalar fontStyle values a doc may carry. 'italic bold' is Konva's
// combined form; anything else ('oblique', '700', …) would silently normalize
// to 'normal' in the renderer — the exact silent fallback this gate forbids.
const FONT_STYLES = ['normal', 'bold', 'italic', 'italic bold'];

// The boolean style flags on text/badge layers. This list IS the Inspector's
// "Stil" toggle vocabulary (one list, UI and gate can never disagree):
// underline, strike (line-through), uppercase (render-time transform, the
// stored text stays raw), shadow (the one TEXT_SHADOW recipe in layerToKonva).
// There is deliberately NO italic flag: the server rasterizer ships no italic
// face, so an authorable slant would render differently in the editor than in
// the published image (and node-canvas un-bolds 'italic bold'). The stored key
// stays TOLERATED below so a doc carrying it still validates — it just maps to
// the upright face on both ends.
const TEXT_STYLE_FLAGS = ['underline', 'strike', 'uppercase', 'shadow'];
const TOLERATED_LEGACY_FLAGS = ['italic'];

// The frozen social_posts.context keys a binding may address. Anything else is
// an unknown bind path and is rejected by validate (well-formed gate) and
// resolves to '' at render time for text.
const CONTEXT_KEYS = [
    'title',
    'description',
    'breed',
    'disciplines',
    'price',
    'location',
    'listingUrl',
    'imageUrls',
];

/**
 * Format a frozen-context price object into badge text. The context price is
 * { amount, currency } where amount is in MINOR units (cents/Rappen, the
 * Sharetribe Money convention — CHF 350 arrives as 35000), currency an ISO code
 * (default CHF).
 *
 * An UNKNOWN price renders '' so the layer draws nothing. A price of ZERO is not
 * unknown — it is a deliberate free offer — and renders "Gratis". This layer used
 * to hide it, quietly deleting the strongest selling point a post can carry. See
 * ./priceText.js for why 0 is trustworthy.
 */
const formatPrice = price => {
    if (!price || typeof price !== 'object') return '';
    const { amount, currency } = price;
    const major = priceMajorUnits(amount);
    if (major === null) return '';
    if (major === 0) return FREE_PRICE_LABEL;
    // No decimals for whole prices (CHF 350), two for fractional (CHF 350.50).
    return `${currency || 'CHF'} ${priceNumberText(major)}`;
};

// A bind path is `context.<key>` optionally with an array index: imageUrls[0].
// We parse exactly that grammar — nothing else is a legal bind.
const BIND_RE = /^context\.([a-zA-Z]+)(?:\[(\d+)\])?$/;

const parseBind = path => {
    if (typeof path !== 'string') return null;
    const m = path.match(BIND_RE);
    if (!m) return null;
    const key = m[1];
    if (!CONTEXT_KEYS.includes(key)) return null;
    const index = m[2] != null ? Number(m[2]) : null;
    return { key, index };
};

/**
 * Resolve a binding path against a frozen context. Returns a string for text
 * binds and a string URL for imageUrls binds. Unknown/missing => '' (text
 * renders empty); the RENDERER decides whether an empty REQUIRED image bind is
 * fatal — this resolver never throws (it is the data layer, not the gate).
 *
 * @param {string} path e.g. 'context.title' | 'context.imageUrls[0]' | 'context.price'
 * @param {object} context frozen social_posts.context
 * @returns {string}
 */
const resolveBinding = (path, context) => {
    const parsed = parseBind(path);
    if (!parsed || !context) return '';
    const { key, index } = parsed;
    const value = context[key];

    if (key === 'imageUrls') {
        const arr = Array.isArray(value) ? value : [];
        const i = index == null ? 0 : index;
        return arr[i] || '';
    }
    if (key === 'disciplines') {
        return Array.isArray(value) ? value.filter(Boolean).join(', ') : value || '';
    }
    if (key === 'price') {
        return formatPrice(value);
    }
    return value == null ? '' : String(value);
};

const isPositiveInt = n => Number.isInteger(n) && n > 0;
const isFiniteNum = n => typeof n === 'number' && Number.isFinite(n);

/**
 * Validate a design doc. Throws on the first defect, returns (undefined) on a
 * valid doc. No mutation, no fallbacks — a malformed doc must never reach the
 * renderer (it would produce a nondeterministic image) or poison getConfig.
 *
 * Rules:
 *  1. version === 1.
 *  2. platform — OPTIONAL legacy metadata. A PRESET owns its own canvas, so a doc
 *     need not carry a platform at all; if present it must be a known one.
 *  3. width/height positive integers (the doc's own canvas); quality, if present,
 *     a number in (0,1]; dpr, if present, a number in (0,4]; background, if present,
 *     a string — these feed the renderer directly (an LLM authors these docs).
 *  4. fonts: array; every entry has a string family + string file.
 *  5. layers: array; every layer has a UNIQUE string id, a known type, and
 *     numeric geometry (x, y, width, height, rotation); an image layer's `fit`,
 *     if present, is "cover" or "contain".
 *  6. image-layer bind paths (and any layer bind) are well-formed (parseBind).
 *  7. text/badge layers naming a fontFamily reference a family declared in
 *     fonts[] (so the server font gate has something to register; a typo'd
 *     family is caught here, not silently substituted at render).
 *  8. text/badge style vocabulary, only WHEN present: fontStyle in FONT_STYLES,
 *     fontWeight numeric, and each TEXT_STYLE_FLAGS entry (italic/underline/
 *     strike/uppercase/shadow) strictly boolean — an unknown style must fail
 *     here, never silently normalize at render.
 *
 * @param {object} doc
 * @returns {void}
 * @throws {Error}
 */
const validateDesignDoc = doc => {
    doc = normalizeDesignDocFonts(doc);
    if (!doc || typeof doc !== 'object') {
        throw new Error('Design doc must be an object');
    }
    if (doc.version !== 1) {
        throw new Error(`Unsupported design doc version "${doc.version}" (expected 1)`);
    }
    // platform is OPTIONAL legacy metadata now (a preset owns its own canvas). It is
    // only constrained WHEN present, so a platform-free preset doc is valid.
    if (doc.platform != null && !PLATFORMS.includes(doc.platform)) {
        throw new Error(`Unknown design platform "${doc.platform}"`);
    }
    if (!isPositiveInt(doc.width) || !isPositiveInt(doc.height)) {
        throw new Error('Design width/height must be positive integers');
    }
    if (
        doc.quality != null &&
        !(typeof doc.quality === 'number' && doc.quality > 0 && doc.quality <= 1)
    ) {
        throw new Error('Design quality must be a number in (0, 1]');
    }
    // dpr scales the rasterized output; the renderer reads `doc.dpr || 2`. Bound it
    // so an LLM-authored doc can't OOM the render (a huge dpr) or NaN it (a string).
    if (doc.dpr != null && !(typeof doc.dpr === 'number' && doc.dpr > 0 && doc.dpr <= 4)) {
        throw new Error('Design dpr must be a number in (0, 4]');
    }
    // background is painted straight into the canvas fill — a string when present.
    if (doc.background != null && typeof doc.background !== 'string') {
        throw new Error('Design background must be a string');
    }
    if (doc.name != null && typeof doc.name !== 'string') {
        throw new Error('Design name must be a string');
    }

    // fonts[]
    if (!Array.isArray(doc.fonts)) {
        throw new Error('Design fonts must be an array');
    }
    const families = new Set();
    for (const font of doc.fonts) {
        if (!font || typeof font.family !== 'string' || !font.family) {
            throw new Error('Every font entry needs a string family');
        }
        if (typeof font.file !== 'string' || !font.file) {
            throw new Error(`Font "${font.family}" needs a string file`);
        }
        families.add(font.family);
    }

    // layers[]
    if (!Array.isArray(doc.layers)) {
        throw new Error('Design layers must be an array');
    }
    const ids = new Set();
    for (const layer of doc.layers) {
        if (!layer || typeof layer !== 'object') {
            throw new Error('Every layer must be an object');
        }
        if (typeof layer.id !== 'string' || !layer.id) {
            throw new Error('Every layer needs a string id');
        }
        if (ids.has(layer.id)) {
            throw new Error(`Duplicate layer id "${layer.id}"`);
        }
        ids.add(layer.id);
        if (!TYPES.includes(layer.type)) {
            throw new Error(`Unknown layer type "${layer.type}" (layer ${layer.id})`);
        }
        for (const k of ['x', 'y', 'width', 'height']) {
            if (!isFiniteNum(layer[k])) {
                throw new Error(`Layer ${layer.id} has non-numeric ${k}`);
            }
        }
        if (layer.rotation != null && !isFiniteNum(layer.rotation)) {
            throw new Error(`Layer ${layer.id} has non-numeric rotation`);
        }

        // Well-formed bind (any layer).
        if (layer.bind != null && parseBind(layer.bind) == null) {
            throw new Error(`Layer ${layer.id} has malformed bind path "${layer.bind}"`);
        }

        // Optional input-binding descriptor: a VARIABLE layer whose content is fed
        // by a wired design-node input. `as` selects what the input fills (image ->
        // src, text -> text, link -> href); `from` (optional) pins it to a specific
        // upstream source node id when several same-kind slots exist (else the
        // runtime matches inbound values to slots by order). design_render.run
        // stamps the resolved value in before render — the renderer never reads
        // `source` (it only sees the stamped src/text/href), so this is authoring
        // metadata, gated here for hygiene (value-6, fail loud at the edge).
        if (layer.source != null) {
            const s = layer.source;
            if (typeof s !== 'object' || Array.isArray(s) || s.kind !== 'input') {
                throw new Error(
                    `Layer ${layer.id} has malformed source (expected { kind:'input', as })`
                );
            }
            if (!INPUT_SLOT_KINDS.includes(s.as)) {
                throw new Error(
                    `Layer ${layer.id} source.as must be one of: ${INPUT_SLOT_KINDS.join(', ')}`
                );
            }
            if (s.from != null && (typeof s.from !== 'string' || !s.from)) {
                throw new Error(`Layer ${layer.id} source.from must be a non-empty string`);
            }
        }

        // Optional href (a tappable link target — the IG-Story CTA). Non-visual
        // metadata read by the publishing step (the native link sticker), not the
        // renderer; a string when present.
        if (layer.href != null && typeof layer.href !== 'string') {
            throw new Error(`Layer ${layer.id} href must be a string`);
        }

        // Image cover pan/zoom (optional): focalX/focalY in [0,1], zoom > 0.
        // Absent => the centered cover crop. Zoom below 1 letterboxes (the crop
        // surface's "fully zoomed out" = the whole photo, i.e. Einpassen).
        // Constrained only WHEN present, so an image layer with no manual crop
        // stays valid (and renders byte-identically).
        if (layer.type === 'image') {
            const inUnit = v => isFiniteNum(v) && v >= 0 && v <= 1;
            // fit (optional): the renderer coerces an unknown value to 'cover'
            // silently (a value-4 fallback inside owned code) — gate it here instead.
            if (layer.fit != null && layer.fit !== 'cover' && layer.fit !== 'contain') {
                throw new Error(`Layer ${layer.id} fit must be "cover" or "contain"`);
            }
            if (layer.focalX != null && !inUnit(layer.focalX)) {
                throw new Error(`Layer ${layer.id} focalX must be a number in [0, 1]`);
            }
            if (layer.focalY != null && !inUnit(layer.focalY)) {
                throw new Error(`Layer ${layer.id} focalY must be a number in [0, 1]`);
            }
            if (layer.zoom != null && !(isFiniteNum(layer.zoom) && layer.zoom > 0)) {
                throw new Error(`Layer ${layer.id} zoom must be a number > 0`);
            }
        }

        if (layer.type === 'text' || layer.type === 'badge') {
            // Alignment vocabulary (see TEXT_ALIGNS): constrained only WHEN
            // present, so a layer that never states an alignment keeps the
            // renderer default (left/top) and stays byte-identical.
            if (layer.align != null && !TEXT_ALIGNS.includes(layer.align)) {
                throw new Error(
                    `Layer ${layer.id} align must be one of: ${TEXT_ALIGNS.join(', ')}`
                );
            }
            if (
                layer.verticalAlign != null &&
                !TEXT_VERTICAL_ALIGNS.includes(layer.verticalAlign)
            ) {
                throw new Error(
                    `Layer ${layer.id} verticalAlign must be one of: ${TEXT_VERTICAL_ALIGNS.join(
                        ', '
                    )}`
                );
            }
            // Font family must be declared (text + badge render text).
            if (layer.fontFamily && !families.has(layer.fontFamily)) {
                throw new Error(
                    `Layer ${layer.id} uses font "${layer.fontFamily}" not declared in fonts[]`
                );
            }
            // Style vocabulary — constrained only WHEN present, so legacy docs
            // that never state these keep validating and render byte-identically.
            if (layer.fontStyle != null && !FONT_STYLES.includes(layer.fontStyle)) {
                throw new Error(
                    `Layer ${layer.id} fontStyle must be one of: ${FONT_STYLES.join(', ')}`
                );
            }
            if (layer.fontWeight != null && !isFiniteNum(layer.fontWeight)) {
                throw new Error(`Layer ${layer.id} fontWeight must be a number`);
            }
            for (const flag of [...TEXT_STYLE_FLAGS, ...TOLERATED_LEGACY_FLAGS]) {
                if (layer[flag] != null && typeof layer[flag] !== 'boolean') {
                    throw new Error(`Layer ${layer.id} ${flag} must be a boolean`);
                }
            }
        }
    }
};

/**
 * collectSlots — the manifest of VARIABLE (input-bound) layers in a doc: every
 * layer whose content is fed by a wired design-node input (`source.kind ===
 * 'input'`), in doc (z-order) order. The design node's on-canvas slot rows and
 * the runtime input→layer binding both read this ONE manifest, so the UI and the
 * render can never disagree about which layers a wire fills. Pure; returns [] for
 * a doc of only baked/context layers.
 *
 * @param {object} doc a design doc (need not be pre-validated)
 * @returns {{layerId:string, from:?string, as:'image'|'text'|'link', label:string}[]}
 */
const collectSlots = doc => {
    const layers = doc && Array.isArray(doc.layers) ? doc.layers : [];
    const slots = [];
    for (const layer of layers) {
        const s = layer && layer.source;
        if (s && s.kind === 'input' && INPUT_SLOT_KINDS.includes(s.as)) {
            slots.push({
                layerId: layer.id,
                from: typeof s.from === 'string' && s.from ? s.from : null,
                as: s.as,
                label: layer.name || layer.id,
            });
        }
    }
    return slots;
};

export {
    TYPES,
    PLATFORMS,
    CONTEXT_KEYS,
    INPUT_SLOT_KINDS,
    TEXT_ALIGNS,
    TEXT_VERTICAL_ALIGNS,
    FONT_STYLES,
    TEXT_STYLE_FLAGS,
    validateDesignDoc,
    resolveBinding,
    formatPrice,
    collectSlots,
    normalizeDesignDocFonts,
};
